/**
 * Daily Report MV2 query API — isolated from MV1 pages/DailyReport.jsx.
 * Per-endpoint fetchers (MV1-style resilience).
 */
import api from "@/lib/api";

export const DAILY_SNAPSHOT_QUERY_KEY = "daily-snapshot";
export const DAILY_SNAPSHOT_DATA_QUERY_KEY = "daily-snapshot-data";
export const DAILY_SNAPSHOT_EXPENSE_QUERY_KEY = "daily-snapshot-expense";
export const DAILY_HISTORY_QUERY_KEY = "daily-history";

function isAbortError(error, signal) {
  if (signal?.aborted) return true;
  return error?.name === "AbortError" || error?.code === "ERR_CANCELED";
}

async function safeSectionFetch(label, signal, fetcher, fallback) {
  try {
    const data = await fetcher();
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    return { data, error: null };
  } catch (error) {
    if (isAbortError(error, signal)) throw error;
    console.error(`Daily Report MV2: failed to fetch ${label}:`, error);
    return { data: fallback, error };
  }
}

export async function fetchDailySnapshotDataFromApi(date, signal) {
  const { data } = await safeSectionFetch(
    "daily-snapshot",
    signal,
    () => api.get(`/daily-snapshot?date=${date}`, { signal }).then((r) => r.data),
    null
  );
  return data;
}

export async function fetchDailySnapshotExpenseFromApi(date, signal) {
  const today = new Date().toISOString().split("T")[0];
  const { data } = await safeSectionFetch(
    "expense-summary",
    signal,
    () => {
      if (date === today) {
        return api.get("/expense-summary/today", { signal }).then((r) => r.data);
      }
      return api
        .get("/expense-summary", { params: { start_date: date, end_date: date }, signal })
        .then((r) => r.data);
    },
    null
  );
  return data;
}

/** Composed fetch — safe section fetchers; kept for guard wiring. */
export async function fetchDailySnapshotFromApi(date, signal) {
  const [snapshot, expenseSummary] = await Promise.all([
    fetchDailySnapshotDataFromApi(date, signal),
    fetchDailySnapshotExpenseFromApi(date, signal),
  ]);
  return { snapshot, expenseSummary };
}

export async function fetchDailyHistoryFromApi(days, signal) {
  const response = await api.get(`/daily-snapshots/history?days=${days}`, { signal });
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
  return {
    historyData: response.data.history || [],
    historySummary: response.data.summary || {},
  };
}
